import {
  EtiquetaDePagina,
  CLASE_TITULO_DE_PAGINA,
  CLASE_SUBTITULO_DE_PAGINA,
} from '@/components/patterns';
import { useState } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Info } from 'lucide-react';
import { apiModules } from '../data/api-endpoints';
import { EndpointCard } from '../components/endpoint-card';

type TabId = string;

export function ApiDocsPage() {
  const [activeTab, setActiveTab] = useState<TabId>(apiModules[0].id);
  const activeModule = apiModules.find((m) => m.id === activeTab)!;

  const baseUrl =
    typeof window !== 'undefined' ? window.location.origin : 'https://tu-servidor.com';

  return (
    <AppLayout>
      <div className="container mx-auto p-8">
        <div className="space-y-6">
          {/* Header */}
          <div>
            <div className="flex items-center gap-3">
              <div className="mb-3">
                <EtiquetaDePagina tono="neutro">Mi empresa</EtiquetaDePagina>
              </div>
              <h1 className={CLASE_TITULO_DE_PAGINA}>API para Desarrolladores</h1>
              <Badge variant="outline" className="font-mono text-xs">
                {baseUrl}/api
              </Badge>
            </div>
            <p className={`mt-2 ${CLASE_SUBTITULO_DE_PAGINA}`}>
              Documentación de las APIs REST del sistema. Todos los endpoints requieren
              autenticación JWT.
            </p>
          </div>

          {/* Auth note */}
          <Card className="border-blue-500/30 bg-blue-500/5">
            <CardContent className="flex items-start gap-3 p-4">
              <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-blue-700">Autenticación requerida</p>
                <p className="text-muted-foreground mt-1">
                  Todas las peticiones requieren el header{' '}
                  <code className="px-1.5 py-0.5 rounded bg-muted font-mono text-xs">
                    Authorization: Bearer {'<token>'}
                  </code>
                  . Obtén tu token con{' '}
                  <code className="px-1.5 py-0.5 rounded bg-muted font-mono text-xs">
                    POST /api/auth/login
                  </code>{' '}
                  enviando email y password.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <div className="flex gap-2 border-b">
            {apiModules.map((mod) => (
              <button
                key={mod.id}
                onClick={() => setActiveTab(mod.id)}
                className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                  activeTab === mod.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {mod.name} ({mod.endpoints.length})
              </button>
            ))}
          </div>

          {/* Module description */}
          <div>
            <p className="text-sm text-muted-foreground mb-1">
              Base URL:{' '}
              <code className="font-mono px-1.5 py-0.5 rounded bg-muted text-xs">
                {activeModule.baseUrl}
              </code>
            </p>
            <p className="text-sm text-muted-foreground">{activeModule.description}</p>
          </div>

          {/* Endpoints */}
          <div className="space-y-3">
            {activeModule.endpoints.map((endpoint, i) => (
              <EndpointCard key={`${endpoint.method}-${endpoint.path}-${i}`} endpoint={endpoint} />
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

export default ApiDocsPage;
